<i18n>
{
  "no": {
    "title": "Registrer ny bruker",
    "ingress" : "Etter innsending av skjema vil du motta en kode på e-post og SMS for å aktivere konto.",
    "ingress-step2" : "Du skal nå motta en kode på SMS til mobilnummeret og en kode på epostadressen du oppga.",
    "create": "Opprett konto",
    "otpSMS": "Engangskode fra SMS",
    "otpEmail": "Engangskode fra epost",
    "validate": "Valider koder",
    "success" : "Kontoen er opprettet. Gå tilbake til",
    "login": "innlogging"
  },
  "en": {
    "title": "Register new account",
    "ingress" : "After submitting you will receive a one time code on email and on SMS to activate the account.",
    "ingress-step2" : "You should now receive a one time code on SMS and on email.",
    "create": "Create account",
    "otpSMS": "OTP from SMS",
    "otpEmail": "OTP from email",
    "validate": "Validate codes",
    "success" : "Account created. Go back to the",
    "login": "log in"
  }
}
</i18n>

<template>
  <div class="page">
    <FormWrapper>
      <div>
        <TnHeading type="h1" size="l" align="center" class="margin-vertical-2xl">
          {{ t('title') }}
        </TnHeading>

        <ErrorMessage :error="error" />

        <div v-if="state === States.BEGIN">
          <TnParagraph size="s" class="margin-bottom-l">{{ t('ingress') }}</TnParagraph>

          <form id="reg" method="post" @submit.prevent="onSubmit">
            <div class="field-stack">
              <TnTextField
                id="username"
                v-model="model.email"
                name="email"
                type="email"
                required
                :label="usernameLabel"
                placeholder="oda@nordmann.no"
              />
              <TnTextField
                id="phone"
                v-model="model.phone"
                name="phone"
                type="tel"
                required
                :label="t('fields.phone')"
                placeholder="+4755555555"
              />
              <TnTextField
                id="password"
                v-model="model.password"
                name="password"
                type="password"
                required
                :label="t('fields.password')"
                placeholder="*****"
              />
              <TnTextField
                id="repeatPassword"
                v-model="model.passwordRepeat"
                name="repeatPassword"
                type="password"
                required
                :label="t('fields.passwordRepeat')"
                placeholder="*****"
              />
            </div>

            <TnButton
              id="btnSubmitRegisterUser"
              type="submit"
              primary
              block
              class="margin-top-l"
              :text="t('create')"
            />

            <PasswordRules />
          </form>
        </div>

        <div v-else-if="state === States.CODE_INPUT">
          <TnParagraph size="s" class="margin-bottom-l">{{ t('ingress-step2') }}</TnParagraph>

          <form id="otp" method="post" @submit.prevent="onSubmitOtp">
            <div class="field-stack">
              <TnTextField
                id="otpSMS"
                v-model="smsOtp"
                name="smsOtp"
                type="text"
                inputmode="numeric"
                autocomplete="one-time-code"
                pattern="[0-9]*"
                required
                :label="t('otpSMS')"
                placeholder="******"
              />
              <TnTextField
                id="otpEmail"
                v-model="emailOtp"
                name="emailOtp"
                type="text"
                inputmode="numeric"
                pattern="[0-9]*"
                required
                :label="t('otpEmail')"
                placeholder="******"
              />
            </div>

            <TnButton
              id="btnOtpSubmit"
              type="submit"
              primary
              block
              class="margin-top-l"
              :text="t('validate')"
            />
          </form>
        </div>

        <TnParagraph v-else size="s">
          {{ t('success') }}
          <TnLink
            id="linkLogin"
            :to="{ name: 'elv-login', query: { request } }"
            :text="t('login')"
          />
        </TnParagraph>
      </div>
    </FormWrapper>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import {
  TnButton,
  TnHeading,
  TnLink,
  TnParagraph,
  TnTextField,
} from '@telenornorgeinternal/telegrafen-vue3-core';

import FormWrapper from '@/components/FormWrapper.vue';
import ErrorMessage from '@/views/elvis/components/errorMessage.vue';
import PasswordRules from '@/views/elvis/components/PasswordRules.vue';
import { beginRegistration, finishRegistration } from '@/views/elvis/api';

const States = { BEGIN: 0, CODE_INPUT: 1, DONE: 2 } as const;

const { t } = useI18n();
const route = useRoute();

const state = ref<number>(States.BEGIN);
const error = ref<unknown>(null);
const smsOtp = ref('');
const emailOtp = ref('');

const model = reactive({ email: '', phone: '', password: '', passwordRepeat: '' });

const request = computed(() => (route.query.request as string) || '');
const usernameLabel = computed(() => `${t('fields.email')} (${t('fields.username')})`);

function onSubmit() {
  error.value = null;

  if (model.password !== model.passwordRepeat) {
    error.value = { type: 'NOT_EQUAL', message: 'Passwords must be equal' };
    return;
  }

  beginRegistration({ ...model })
    .then(() => {
      smsOtp.value = '';
      emailOtp.value = '';
      state.value = States.CODE_INPUT;
      error.value = null;
    })
    .catch((reason) => (error.value = reason));
}

function onSubmitOtp() {
  error.value = null;

  finishRegistration(model.email, smsOtp.value, emailOtp.value)
    .then(() => {
      state.value = States.DONE;
      error.value = null;
    })
    .catch((reason) => (error.value = reason));
}
</script>
